// The translator without React (brain/engineering/docs/white-label-and-i18n.md §4): catalogues, fallback
// to English per message, and Intl formatters. Lingui's core (MIT, ~2 KB gzipped) formats compiled ICU messages.
import { catalogues as sharedCatalogues } from '../locales/index.generated';
import { launchCatalogue } from './errors';
import { setupI18n, type I18n, type Messages } from '@lingui/core';
import { brandName } from '../brand/brand';
import { negotiateLocale, regionalLocale, setActiveLocale, SOURCE_LOCALE, SUPPORTED_LOCALES } from './locale';

/** A compiled catalogue, as written by ui/tools/i18n.mjs. `status` says whether a native editor has reviewed it. */
export interface Catalogue {
  messages: Messages;
  status: 'source' | 'machine-draft' | 'reviewed';
}

/** A language loaded when chosen; its review status is known before loading (the language menu shows it). */
export interface LazyCatalogue {
  status: Catalogue['status'];
  load: () => Promise<Catalogue>;
}

/** The app's catalogues (written by ui/tools/i18n.mjs as locales/index.generated.ts): English bundled, the others lazy. */
export interface Catalogues {
  en: Catalogue;
  [locale: string]: Catalogue | LazyCatalogue;
}

/** A language's review status without loading it. */
export function catalogueStatus(catalogues: Catalogues, locale: string): Catalogue['status'] | null {
  return catalogues[locale]?.status ?? null;
}

export type Values = Record<string, unknown>;

/** Formatting for the active locale: numbers, money (always with its own currency), dates and lists. */
export interface Formatters {
  number: (n: number, o?: Intl.NumberFormatOptions) => string;
  currency: (amount: number, currency: string) => string;
  date: (d: Date | number, o?: Intl.DateTimeFormatOptions) => string;
  list: (items: readonly string[], type?: 'conjunction' | 'disjunction') => string;
}

/** Formatters for an interface language (with the browser's region where it matches, see regionalLocale). */
export function formatters(language: string): Formatters {
  const locale = regionalLocale(language);
  return {
    number: (n, o) => new Intl.NumberFormat(locale, o).format(n),
    currency: (amount, currency) => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount),
    date: (d, o) => new Intl.DateTimeFormat(locale, o ?? { dateStyle: 'medium' }).format(d),
    list: (items, type = 'conjunction') => new Intl.ListFormat(locale, { style: 'long', type }).format(items),
  };
}

/** A message ready for its values: `text` has them held out, and `fill` puts them into any part of it. */
export interface HeldMessage { text: string; fill: (part: string) => string }

/** A compiled message's arguments: those it prints as they are, and those a formatter takes (plural, number, date). */
function messageArgs(tokens: unknown, found = { printed: new Set<string>(), formatted: new Set<string>() }) {
  if (!Array.isArray(tokens)) return found;
  for (const token of tokens) {
    if (!Array.isArray(token)) continue;
    const [name, type, format] = token as [string, unknown, Record<string, unknown> | undefined];
    if (!type) found.printed.add(name);
    else {
      found.formatted.add(name);
      if (format && typeof format === 'object') for (const branch of Object.values(format)) messageArgs(branch, found);
    }
  }
  return found;
}

const MARKS = /[\uE000-\uF8FF]/g;

/** A translator for one app. Draft languages require explicit internal preview, including direct activation. */
export function createTranslator(catalogues: Catalogues, defaults: () => Values = () => ({}), { internalPreview = false }: { internalPreview?: boolean } = {}) {
  const english = { ...sharedCatalogues.en.messages, ...launchCatalogue(SOURCE_LOCALE), ...catalogues.en.messages };
  const i18n: I18n = setupI18n({ locale: SOURCE_LOCALE, messages: { [SOURCE_LOCALE]: english } });
  const status: Record<string, Catalogue['status']> = { en: 'source' };
  const available = SUPPORTED_LOCALES.filter((l) => l in catalogues && (internalPreview || catalogues[l]?.status !== 'machine-draft'));
  async function activate(locale: string): Promise<string> {
    const want = negotiateLocale([locale], available);
    if (!(want in status)) {
      const c = catalogues[want];
      try {
        const cat = c && 'load' in c ? await c.load() : c;
        if (cat) {
          // English underneath, per message: a missing translation shows the source, never an id.
          const shared = sharedCatalogues[want];
          const sharedCat = shared && (internalPreview || shared.status !== 'machine-draft')
            ? ('load' in shared ? await shared.load() : shared) : undefined;
          i18n.load(want, { ...english, ...launchCatalogue(want), ...sharedCat?.messages, ...cat.messages });
          status[want] = cat.status;
        }
      } catch {
        return activate(SOURCE_LOCALE);   // a catalogue that fails to load leaves the app in English
      }
    }
    const active = want in status ? want : SOURCE_LOCALE;
    setActiveLocale(active);   // before activate(): listeners re-render and read it
    i18n.activate(active);
    return i18n.locale;
  }
  /**
   * The message, with every printed value held out of Lingui, whatever its type or source: Lingui decodes `\\uXXXX` in the
   * finished text, and tags are read from it, so a value inside could change its words or become a tag. Each value waits
   * behind a mark this message doesn't otherwise hold, and `fill` makes one pass, so a value is never read again.
   */
  const held = (id: string, values?: Values): HeldMessage => {
    const all: Values = { brand: brandName(), ...defaults(), ...values };
    const { printed, formatted } = messageArgs(i18n.messages[id]);
    // A count that also prints inside its own plural must stay real to choose the branch, so it can only be a number.
    for (const name of formatted) if (printed.has(name) && typeof all[name] !== 'number') all[name] = Number(all[name]);
    const args = [...printed].filter((name) => !formatted.has(name) && all[name] != null);
    if (!args.length) return { text: i18n._(id, all), fill: (part) => part };
    const own = i18n._(id, { ...all, ...Object.fromEntries(args.map((name) => [name, ''])) });
    const waiting = new Map<string, string>();
    const marked: Values = { ...all };
    let code = 0xE000;
    for (const name of args) {
      while (own.includes(String.fromCharCode(code))) code += 1;
      const mark = String.fromCharCode(code);
      code += 1;
      waiting.set(mark, String(all[name]));
      marked[name] = mark;
    }
    return { text: i18n._(id, marked), fill: (part) => part.replace(MARKS, (c) => waiting.get(c) ?? c) };
  };
  const t = (id: string, values?: Values): string => { const message = held(id, values); return message.fill(message.text); };
  return { i18n, t, held, activate, available, status: () => status[i18n.locale] ?? 'source' };
}
